(() => {
  'use strict';

  const ROOT_ID = 'rv-recently-viewed-drawer-root';
  const BLOCK_ID = 'rv-upsell-block';
  const SESSION_INIT_KEY = 'rv_session_init';
  const SESSION_STARTED_KEY = 'rv_session_started_with_recents';
  const SESSION_AUTOOPEN_KEY = 'rv_auto_open_done';

  const DEFAULTS = {
    enabled: false,
    anchorSelector: '.upez--drawer-inner',
    insertMode: 'afterend',
    maxItems: 10,
    excludeCurrentProduct: true,
    showReturningVisitorsOnly: true,
    autoOpenReturningSession: false,
    autoOpenOncePerSession: true,
    autoOpenOnlyIfHasOtherProducts: true,
    titleText: 'Continue where you left off:',
    emptyText: 'Recently viewed products will appear here.',
    addButtonText: 'Add',
    showCompareAt: true,
    showArrows: true,
    moneyFormat: '${{amount}}',
    currencyIso: ''
  };

  const root = document.getElementById(ROOT_ID);
  if (!root) return;

  let cfg = DEFAULTS;
  try {
    const raw = root.getAttribute('data-rv-config') || '{}';
    cfg = { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    cfg = { ...DEFAULTS };
  }

  if (!cfg.enabled) return;

  // Avoid annoying behavior in Theme Editor
  const inDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

  // ===== Utils
  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  const getCurrentHandle = () => {
    const parts = location.pathname.split('/').filter(Boolean);
    const i = parts.indexOf('products');
    return (i >= 0 && parts[i + 1])
      ? decodeURIComponent(parts[i + 1].split(/[?#]/)[0])
      : null;
  };

  const uniq = (arr) => Array.from(new Set(arr));

  const safeJsonParse = (s, fallback) => { try { return JSON.parse(s); } catch { return fallback; } };

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isMobile = () => window.innerWidth < 768;

  // ===== Money formatting
  const formatMoney = (cents) => {
    const n = Number.isFinite(cents) ? cents : 0;

    // Shopify.formatMoney if available
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(n, cfg.moneyFormat);
      } catch {}
    }

    // Fallback: $ symbol at the beginning
    const amount = (n / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `$${amount}`;
  };

  // ===== Storage (localStorage with safe fallback)
  const Mem = { data: {} };
  const Storage = {
    getRaw(k) {
      try { return localStorage.getItem(k); } catch { return Mem.data[k] ?? null; }
    },
    setRaw(k, v) {
      try { localStorage.setItem(k, v); } catch {}
      try { sessionStorage.setItem(k, v); } catch {}
      Mem.data[k] = v;
    },
    getList(k) {
      const raw = this.getRaw(k);
      if (!raw || raw === 'null' || raw === 'undefined') return [];
      const parsed = safeJsonParse(raw, []);
      const arr = Array.isArray(parsed) ? parsed : [];
      // Always return deduplicated array
      return uniq(arr.filter(Boolean));
    },
    setList(k, arr) {
      this.setRaw(k, JSON.stringify(Array.isArray(arr) ? arr : []));
    },
    sanitize(k) {
      const raw = this.getRaw(k);
      if (raw === null) return;
      const parsed = safeJsonParse(raw, []);
      if (!Array.isArray(parsed)) this.setList(k, []);
    }
  };

  const KEY_PRIMARY = 'rv_upnova_recent_v1';
  const KEY_MIRROR = 'recentlyViewed_v1';
  Storage.sanitize(KEY_PRIMARY);
  Storage.sanitize(KEY_MIRROR);
  // ===== Initialize session flags FIRST (before tracking current product)
  // This captures the true "returning visitor" state before we modify storage
  (() => {
    const recentsAtBoot = Storage.getList(KEY_PRIMARY);
    const startedWithRecents = recentsAtBoot.length > 0;
    try {
      if (!sessionStorage.getItem(SESSION_INIT_KEY)) {
        sessionStorage.setItem(SESSION_INIT_KEY, '1');
        sessionStorage.setItem(SESSION_STARTED_KEY, startedWithRecents ? '1' : '0');
      }
    } catch {
      // sessionStorage unavailable, will fall back to localStorage checks
    }
  })();
  // ===== Session flags (only auto-open on a “returning” session)
  const getSessionFlags = () => {
    const recentsAtBoot = Storage.getList(KEY_PRIMARY);
    let startedWithRecents = recentsAtBoot.length > 0;

    try {
      if (!sessionStorage.getItem(SESSION_INIT_KEY)) {
        sessionStorage.setItem(SESSION_INIT_KEY, '1');
        sessionStorage.setItem(SESSION_STARTED_KEY, startedWithRecents ? '1' : '0');
      } else {
        startedWithRecents = sessionStorage.getItem(SESSION_STARTED_KEY) === '1';
      }
    } catch {
      // If sessionStorage unavailable, keep startedWithRecents derived from recentsAtBoot
    }

    const hasAutoOpenedThisSession = () => {
      try { return sessionStorage.getItem(SESSION_AUTOOPEN_KEY) === '1'; } catch { return false; }
    };

    const markAutoOpenedThisSession = () => {
      try { sessionStorage.setItem(SESSION_AUTOOPEN_KEY, '1'); } catch {}
    };

    return { startedWithRecents, hasAutoOpenedThisSession, markAutoOpenedThisSession };
  };

  // ===== Recently viewed tracking (URL-only)
  const saveHandle = (handle) => {
    if (!handle) return;
    const before = Storage.getList(KEY_PRIMARY);
    const next = uniq([handle, ...before.filter(h => h !== handle)]).slice(0, cfg.maxItems);
    Storage.setList(KEY_PRIMARY, next);
    Storage.setList(KEY_MIRROR, next);
  };

  // Track only on PDP (AFTER session init above)
  const currentHandle = getCurrentHandle();
  if (currentHandle) saveHandle(currentHandle);

  // ===== DOM builders (no innerHTML for product text)
  const ARROW_SVG = `
    <svg class="rv-icon" width="7" height="13" viewBox="0 0 7 13" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.13965 12.1328L0.958068 6.58112L6.13965 1.02943" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>`;

  const makeBlock = () => {
    const el = document.createElement('section');
    el.id = BLOCK_ID;
    el.className = 'rv';

    const header = document.createElement('div');
    header.className = 'rv-header';

    const title = document.createElement('div');
    title.className = 'rv-title';
    title.textContent = cfg.titleText;

    const nav = document.createElement('div');
    nav.className = 'rv-nav';

    const prev = document.createElement('button');
    prev.className = 'rv-arrow rv-prev';
    prev.type = 'button';
    prev.setAttribute('aria-label', 'Previous');
    prev.innerHTML = ARROW_SVG;

    const next = document.createElement('button');
    next.className = 'rv-arrow rv-next';
    next.type = 'button';
    next.setAttribute('aria-label', 'Next');
    next.innerHTML = ARROW_SVG;

    if (cfg.showArrows) {
      nav.appendChild(prev);
      nav.appendChild(next);
    }

    header.appendChild(title);
    header.appendChild(nav);

    const wrap = document.createElement('div');
    wrap.className = 'rv-track-wrap';

    const track = document.createElement('div');
    track.className = 'rv-track';
    track.setAttribute('role', 'region');
    track.setAttribute('aria-label', 'Recently viewed');

    wrap.appendChild(track);

    el.appendChild(header);
    el.appendChild(wrap);

    return el;
  };

  // ===== Product fetch (with cache + concurrency limit)
  const productCache = new Map();

  const normalizeCents = (raw) => {
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string') {
      const v = Number(raw);
      if (Number.isFinite(v)) return Math.round(v * 100);
    }
    return 0;
  };

  const fetchProductJson = async (handle) => {
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);

    const p = (async () => {
      try {
        const r = await fetch(`/products/${encodeURIComponent(handle)}.json`, { credentials: 'same-origin' });
        if (!r.ok) return null;
        const json = await r.json();
        return json && json.product ? json.product : null;
      } catch {
        return null;
      }
    })();

    productCache.set(handle, p);
    return p;
  };

  const mapLimit = async (items, limit, iterator) => {
    const results = [];
    let idx = 0;

    const workers = new Array(Math.max(1, limit)).fill(null).map(async () => {
      while (idx < items.length) {
        const i = idx++;
        results[i] = await iterator(items[i], i);
      }
    });

    await Promise.all(workers);
    return results;
  };

  const openCartIfPossible = () => {
    try {
      if (window.UPEZ && typeof window.UPEZ.openCart === 'function') {
        window.UPEZ.openCart.call(window.UPEZ);
      }
    } catch {}
  };

  const addToCart = async (variantId) => {
    const vid = Number(variantId);
    if (!Number.isFinite(vid) || vid <= 0) return false;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: vid, quantity: 1 })
      });
      return res.ok;
    } catch {
      return false;
    }
  };

  const renderItems = async (track, handles) => {
    // Clear existing content completely
    track.innerHTML = '';
    
    // Deduplicate handles array
    const uniqueHandles = uniq(handles.filter(Boolean));
    
    // Debug: check for duplicates in input
    if (handles.length !== uniqueHandles.length) {
      console.warn('RV Drawer: Removed', handles.length - uniqueHandles.length, 'duplicate handles before rendering');
    }

    if (!uniqueHandles.length) {
      const empty = document.createElement('div');
      empty.className = 'rv-empty';
      empty.textContent = cfg.emptyText;
      track.appendChild(empty);
      return;
    }

    const products = await mapLimit(uniqueHandles, 4, async (h) => {
      const prod = await fetchProductJson(h);
      return prod ? { handle: h, product: prod } : null;
    });

    // Track rendered handles and product IDs to prevent duplicates
    const renderedHandles = new Set();
    const renderedIds = new Set();

    products.filter(Boolean).forEach(({ handle, product }) => {
      // Skip if already rendered (prevent duplicates by handle OR product ID)
      if (renderedHandles.has(handle) || renderedIds.has(product.id)) return;
      renderedHandles.add(handle);
      renderedIds.add(product.id);
      const v = (product.variants || []).find(x => x.available) || (product.variants || [])[0];
      if (!v) return;

      const imgSrc = (product.images && product.images[0] && (product.images[0].src || product.images[0].url)) || '';

      const priceCents = normalizeCents(v.price ?? product.price);
      const compareCents = normalizeCents(v.compare_at_price ?? product.compare_at_price);

      const card = document.createElement('div');
      card.className = 'rv-item';

      const link = document.createElement('a');
      link.className = 'rv-link';
      link.href = `/products/${product.handle}`;

      const img = document.createElement('img');
      img.className = 'rv-img';
      img.loading = 'lazy';
      img.alt = product.title || '';
      if (imgSrc) img.src = imgSrc;

      const name = document.createElement('div');
      name.className = 'rv-name';
      name.textContent = product.title || '';

      const priceRow = document.createElement('div');
      priceRow.className = 'rv-price';

      const cur = document.createElement('span');
      cur.className = 'rv-price-current';
      cur.textContent = formatMoney(priceCents);

      priceRow.appendChild(cur);

      if (cfg.showCompareAt && compareCents > priceCents) {
        const cmp = document.createElement('span');
        cmp.className = 'rv-price-compare';
        cmp.textContent = formatMoney(compareCents);
        priceRow.appendChild(cmp);
      }

      link.appendChild(img);
      link.appendChild(name);
      link.appendChild(priceRow);

      const actions = document.createElement('div');
      actions.className = 'rv-actions';

      const btn = document.createElement('button');
      btn.className = 'rv-btn';
      btn.type = 'button';
      btn.textContent = cfg.addButtonText;
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        btn.disabled = true;
        await addToCart(v.id);
        btn.disabled = false;
      });

      actions.appendChild(btn);

      card.appendChild(link);
      card.appendChild(actions);
      track.appendChild(card);
    });

    // arrows
    const wrap = track.closest('#' + BLOCK_ID);
    const prev = wrap && wrap.querySelector('.rv-prev');
    const next = wrap && wrap.querySelector('.rv-next');
    const step = () => {
      const first = track.querySelector('.rv-item');
      const styles = getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '12');
      return (first ? first.offsetWidth : 220) + (Number.isFinite(gap) ? gap : 12);
    };

    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    if (prev) prev.onclick = () => track.scrollBy({ left: -step(), behavior });
    if (next) next.onclick = () => track.scrollBy({ left: step(), behavior });
  };

  const ensureInjected = async (anchor) => {
    if (!anchor) return;

    // Only inject on mobile devices (< 768px)
    if (!isMobile()) return;

    // Check if should show to returning visitors only
    if (cfg.showReturningVisitorsOnly) {
      const { startedWithRecents } = getSessionFlags();
      if (!startedWithRecents) return;
    }

    let block = document.getElementById(BLOCK_ID);
    if (!block) {
      block = makeBlock();
      anchor.insertAdjacentElement(cfg.insertMode, block);
    } else {
      // Remove old block completely to prevent duplicate rendering
      const oldBlock = block;
      oldBlock.remove();
      
      // Create fresh block
      block = makeBlock();
      anchor.insertAdjacentElement(cfg.insertMode, block);
    }

    const track = block.querySelector('.rv-track');
    if (!track) return;

    const recents = Storage.getList(KEY_PRIMARY);
    const current = getCurrentHandle();

    let toShow = recents.slice(0, cfg.maxItems);
    if (cfg.excludeCurrentProduct && current) {
      toShow = toShow.filter(h => h !== current).slice(0, cfg.maxItems);
    }

    // Ensure no duplicates before rendering
    toShow = uniq(toShow);

    await renderItems(track, toShow);
  };

  // ===== Auto-open logic (optional)
  const maybeAutoOpen = async () => {
    if (!cfg.autoOpenReturningSession) return;
    if (inDesignMode) return;

    // Only auto-open on mobile devices (< 768px)
    if (!isMobile()) return;

    const { startedWithRecents, hasAutoOpenedThisSession, markAutoOpenedThisSession } = getSessionFlags();
    if (!startedWithRecents) return;
    if (cfg.autoOpenOncePerSession && hasAutoOpenedThisSession()) return;

    const recents = Storage.getList(KEY_PRIMARY);
    if (!recents.length) return;

    if (cfg.autoOpenOnlyIfHasOtherProducts) {
      const current = getCurrentHandle();
      const others = current ? recents.filter(h => h !== current) : recents;
      if (!others.length) return;
    }

    // Wait briefly for UPEZ availability
    for (let i = 0; i < 40; i++) {
      if (window.UPEZ && typeof window.UPEZ.openCart === 'function') break;
      await sleep(50);
    }

    if (!window.UPEZ || typeof window.UPEZ.openCart !== 'function') return;

    try {
      // Wait for cart anchor to exist in DOM
      let anchor = null;
      for (let i = 0; i < 40; i++) {
        anchor = document.querySelector(cfg.anchorSelector);
        if (anchor) break;
        await sleep(50);
      }
      
      if (!anchor) return;
      
      // Inject the section first
      await ensureInjected(anchor);
      
      // Now open the cart drawer
      window.UPEZ.openCart.call(window.UPEZ);
      
      markAutoOpenedThisSession();
    } catch {}
  };

  // ===== Anchor discovery (MutationObserver so it works when drawer is rendered later)
  let lastAnchor = null;
  let isRendering = false;
  
  const tryFindAnchorAndInject = async () => {
    const anchor = document.querySelector(cfg.anchorSelector);
    if (!anchor) return;
    
    // Prevent concurrent renders
    if (isRendering) return;

    if (anchor !== lastAnchor) {
      lastAnchor = anchor;
      isRendering = true;
      try {
        await ensureInjected(anchor);
      } finally {
        isRendering = false;
      }
    }
  };

  const startObserver = () => {
    // Debounce the observer callback
    let timeout = null;
    const obs = new MutationObserver(() => {
      clearTimeout(timeout);
      timeout = setTimeout(() => tryFindAnchorAndInject(), 100);
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });

    // Also attempt immediately (in case anchor is already there)
    tryFindAnchorAndInject();
  };

  // Boot
  document.addEventListener('DOMContentLoaded', () => {
    startObserver();
    maybeAutoOpen();
  });
})();
